import { useQuery } from '@apollo/client';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppointmentTypeFilterButton, ClosedMarketPrompt, FiltersContainer } from './Time.styled';
import { BookingCalendarTime } from '../../components/BookingCalendarTime/BookingCalendarTime';
import {
  DEFAULT_FALLBACK,
  MissingTimeSlotsFallback
} from '../../components/BookingCalendarTime/MissingTimeSlotsFallback';
import { Camera } from 'assets/icons/Icons';
import { Loader } from 'components/Loader/Loader';
import { useFeatureFlags } from 'hooks/useFeatureFlags/useFeatureFlags';
import { usePatient } from 'hooks/usePatient/usePatient';
import { AppointmentType, Clinic, Lab, TimeSlot } from 'kb-shared/types';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { formatPhoneNumber } from 'kb-shared/utilities/phones';
import { AppointmentTypeFilter } from 'screens/Book/Book.types';
import { CameraIconContainer } from 'screens/Book/components/BookingCalendarTime/components/DaySection.styled';
import {
  TIMESLOTS_AVAILABLE_QUERY,
  TimeSlotsAvailableQueryResponse
} from 'screens/Book/components/BookingCalendarTime/graphql/queries';
import { Navigation } from 'screens/Book/components/Navigation/Navigation';
import { memberServicesPhone, memberServicesPhoneHref } from 'screens/Book/memberServicesPhone';
import { StepHeading, StepHeadingContainer, StepWrapper } from 'screens/Book/styles';
import { getMonthStartDateTime } from 'utilities/formatDate';
import { useBreakpoints } from 'utilities/useBreakpoints';

import { splitToInClinicAndVirtual } from './Time.utils';

export const TimeStep = ({
  providerId,
  date,
  appointmentType,
  selectedClinic,
  selectedLab,
  selectedTimeSlot,
  hideInClinicSlots,
  onTimeSlotSelect,
  onBack
}: {
  providerId?: number;
  date?: string;
  appointmentType?: AppointmentType;
  selectedClinic?: Clinic;
  selectedLab?: Lab;
  selectedTimeSlot?: TimeSlot;
  // Closed-market NPV: virtual only. Resolved by useBookingState before this step renders.
  hideInClinicSlots: boolean;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
  onBack?: () => void;
}) => {
  if (!appointmentType) return DEFAULT_FALLBACK;

  const location = useLocation();
  const navigate = useNavigate();
  const { isMinTablet } = useBreakpoints();
  const { patient } = usePatient();
  const { ppNetworkClinicBooking } = useFeatureFlags();
  const memberPhone = memberServicesPhone(patient?.patientMembership?.employerPhone);
  const noSlotsPhone = ppNetworkClinicBooking ? memberPhone : undefined;

  const [loadingTimeSlots, setLoadingTimeSlots] = useState(true);

  const handleLoadingTimeSlots = () => setLoadingTimeSlots(false);

  const labId = selectedLab ? parseInt(selectedLab.id) : null;
  const locationId = selectedClinic ? Number(selectedClinic.id) : null;
  const startDateTime = date ? `${date}T00:00:00.000Z` : getMonthStartDateTime();
  const endDateTime = date ? `${date}T23:59:59.999Z` : undefined;

  const { data, loading: appointmentDataloading } = useQuery<TimeSlotsAvailableQueryResponse>(
    TIMESLOTS_AVAILABLE_QUERY,
    {
      variables: {
        appointmentTypeId: parseInt(appointmentType.id),
        locationId: locationId ? locationId : null,
        labId: labId,
        startDateTime: startDateTime,
        ...(endDateTime && { endDateTime: endDateTime }),
        providerId: providerId
      },
      onError: error => BugTracker.notify(error, 'AppointmentTimeSlotsAvailableQuery')
    }
  );

  const linkedAppointment = appointmentType.inClinicOrVirtualEquivalent;

  const { data: linkedAppointmentData, loading: loadingLinkedAppointmentData } =
    useQuery<TimeSlotsAvailableQueryResponse>(TIMESLOTS_AVAILABLE_QUERY, {
      variables: {
        appointmentTypeId: parseInt(linkedAppointment?.id ?? ''),
        locationId: locationId ? locationId : null,
        labId: labId,
        startDateTime: startDateTime,
        ...(endDateTime && { endDateTime: endDateTime }),
        providerId: providerId
      },
      onError: error => BugTracker.notify(error, 'LinkedAppointmentTimeSlotsAvailableQuery'),
      skip: !linkedAppointment
    });

  const updatedAppointmentType: AppointmentType = getUpdatedAppointmentType(
    appointmentType,
    linkedAppointment,
    data,
    linkedAppointmentData
  );

  const filterState = getFilterState(updatedAppointmentType);
  const isAllFilterDisabled = hideInClinicSlots || filterState.isAllFilterDisabled;
  const isInClinicFilterDisabled = hideInClinicSlots || filterState.isInClinicFilterDisabled;
  const isVirtualFilterDisabled = hideInClinicSlots ? false : filterState.isVirtualFilterDisabled;

  const defaultFilter = hideInClinicSlots
    ? 'virtual'
    : getDefaultFilter(isAllFilterDisabled, isInClinicFilterDisabled, isVirtualFilterDisabled);

  const [selectedFilter, setSelectedFilter] = useState(
    new URLSearchParams(location.search).get('appointment_types') || defaultFilter
  );

  const updateAppointmentTypeFilter = (appointmentTypeFilter: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('appointment_types', appointmentTypeFilter);
    setSelectedFilter(appointmentTypeFilter);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    updateAppointmentTypeFilter(defaultFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilter]);

  useLayoutEffect(() => {
    if (!hideInClinicSlots) return;

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('appointment_types') === 'virtual') return;

    searchParams.set('appointment_types', 'virtual');
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  }, [hideInClinicSlots, location.pathname, location.search, navigate]);

  useEffect(() => {
    const newFilter =
      new URLSearchParams(location.search).get('appointment_types') || defaultFilter;
    setSelectedFilter(newFilter);
  }, [location.search, defaultFilter]);

  // TODO: remove this useEffect once the booking session tracking is implemented on BE
  // Used to stop unwanted behavior when patients use the browser's back button on the success step
  useEffect(() => {
    if (localStorage.getItem('BOOKING_SUCCEEDED') !== null) {
      localStorage.removeItem('BOOKING_SUCCEEDED');
    }
  }, []);

  const bothAppointmentTypesHaveAvailableTimeslots =
    (!updatedAppointmentType.virtual && updatedAppointmentType.hasTimeSlots) ||
    (updatedAppointmentType.inClinicOrVirtualEquivalent != null &&
      !updatedAppointmentType.inClinicOrVirtualEquivalent.virtual &&
      updatedAppointmentType.inClinicOrVirtualEquivalent.hasTimeSlots);

  const loadingAppointmentData = appointmentDataloading || loadingLinkedAppointmentData;

  if (loadingAppointmentData) return <Loader container />;

  const shouldShowFallback =
    !hideInClinicSlots &&
    !loadingAppointmentData &&
    !selectedClinic &&
    bothAppointmentTypesHaveAvailableTimeslots;

  if (shouldShowFallback) {
    return (
      <StepWrapper>
        <MissingTimeSlotsFallback phone={noSlotsPhone} />
        {onBack && <Navigation onBack={onBack} step="time" />}
      </StepWrapper>
    );
  }

  const virtualHasTimeSlots = updatedAppointmentType.virtual
    ? updatedAppointmentType.hasTimeSlots
    : updatedAppointmentType.inClinicOrVirtualEquivalent?.hasTimeSlots;
  const timeSlotsAvailable = hideInClinicSlots
    ? Boolean(virtualHasTimeSlots)
    : Boolean(
        updatedAppointmentType.hasTimeSlots ||
        updatedAppointmentType.inClinicOrVirtualEquivalent?.hasTimeSlots
      );

  const shouldShowBookingCalendarTime =
    updatedAppointmentType &&
    (selectedClinic ||
      updatedAppointmentType?.virtual ||
      (updatedAppointmentType.inClinicOrVirtualEquivalent &&
        updatedAppointmentType.inClinicOrVirtualEquivalent.virtual));

  const renderNavigation = () => {
    if (
      (loadingAppointmentData || loadingTimeSlots) &&
      shouldShowBookingCalendarTime &&
      timeSlotsAvailable
    )
      return null;
    return onBack && <Navigation onBack={onBack} step="time" />;
  };

  return (
    <StepWrapper>
      {timeSlotsAvailable ? (
        <>
          <StepHeadingContainer>
            <StepHeading>What date & time works best for you?</StepHeading>
          </StepHeadingContainer>
          <FiltersContainer>
            {isMinTablet ? 'Filter Appointments' : 'Filter'}:
            <AppointmentTypeFilterButton
              selected={!hideInClinicSlots && selectedFilter === 'all'}
              onClick={() => {
                if (isAllFilterDisabled) return;
                updateAppointmentTypeFilter('all');
              }}
              disabled={isAllFilterDisabled}
            >
              All
            </AppointmentTypeFilterButton>
            <AppointmentTypeFilterButton
              selected={!hideInClinicSlots && selectedFilter === 'inClinic'}
              onClick={() => {
                if (isInClinicFilterDisabled) return;
                updateAppointmentTypeFilter('inClinic');
              }}
              disabled={isInClinicFilterDisabled}
            >
              In-Clinic
            </AppointmentTypeFilterButton>
            <AppointmentTypeFilterButton
              selected={hideInClinicSlots || selectedFilter === 'virtual'}
              onClick={() => {
                if (isVirtualFilterDisabled) return;
                updateAppointmentTypeFilter('virtual');
              }}
              disabled={isVirtualFilterDisabled}
            >
              Virtual{' '}
              <CameraIconContainer>
                <Camera type="solid" width={12} height={12} />
              </CameraIconContainer>
            </AppointmentTypeFilterButton>
          </FiltersContainer>
          {shouldShowBookingCalendarTime && (
            <BookingCalendarTime
              handleTimeSlotsLoaded={handleLoadingTimeSlots}
              date={date}
              labId={selectedLab && parseInt(selectedLab.id)}
              locationId={selectedClinic ? selectedClinic.id : null}
              appointmentType={updatedAppointmentType}
              selectedTimeSlot={selectedTimeSlot ?? null}
              providerId={providerId}
              currentDayCanBeSelected={updatedAppointmentType.name !== 'AMH Blood Draw'}
              selectedLabTimeZone={selectedClinic?.timeZone || selectedLab?.timeZone || ''}
              onSelectTimeSlot={(slot: TimeSlot | null) => {
                if (slot != null) {
                  onTimeSlotSelect(slot);
                }
              }}
            />
          )}
          {hideInClinicSlots && (
            <ClosedMarketPrompt>
              If you have questions about where to start, please call us at{' '}
              <a href={memberServicesPhoneHref(memberPhone)}>{formatPhoneNumber(memberPhone)}</a>
            </ClosedMarketPrompt>
          )}
        </>
      ) : (
        <MissingTimeSlotsFallback phone={noSlotsPhone} />
      )}
      {renderNavigation()}
    </StepWrapper>
  );
};

const getDefaultFilter = (
  isAllFilterDisabled: boolean,
  isInClinicFilterDisabled: boolean,
  isVirtualFilterDisabled: boolean
): AppointmentTypeFilter => {
  if (isAllFilterDisabled && !isInClinicFilterDisabled) {
    return 'inClinic';
  }
  if (isAllFilterDisabled && isInClinicFilterDisabled && !isVirtualFilterDisabled) {
    return 'virtual';
  }
  return 'all';
};

const getUpdatedAppointmentType = (
  appointment: AppointmentType,
  linkedAppointment: AppointmentType | null,
  appointmentAvailability?: TimeSlotsAvailableQueryResponse,
  linkedAppointmentAvailability?: TimeSlotsAvailableQueryResponse
): AppointmentType => {
  return {
    ...appointment,
    hasTimeSlots: appointmentAvailability?.hasTimeSlots,
    inClinicOrVirtualEquivalent: linkedAppointment
      ? {
          ...linkedAppointment,
          hasTimeSlots: linkedAppointmentAvailability?.hasTimeSlots
        }
      : null
  };
};

const getFilterState = (
  appointmentType: AppointmentType
): {
  isAllFilterDisabled: boolean;
  isVirtualFilterDisabled: boolean;
  isInClinicFilterDisabled: boolean;
} => {
  const { virtualAppointment, inClinicAppointment } = splitToInClinicAndVirtual(appointmentType);

  const isAllFilterDisabled = !!(
    !appointmentType.inClinicOrVirtualEquivalent ||
    !appointmentType.hasTimeSlots ||
    (appointmentType.inClinicOrVirtualEquivalent &&
      !appointmentType?.inClinicOrVirtualEquivalent.hasTimeSlots)
  );

  return {
    isAllFilterDisabled,
    isVirtualFilterDisabled:
      isAllFilterDisabled && (!virtualAppointment || !virtualAppointment.hasTimeSlots),
    isInClinicFilterDisabled:
      isAllFilterDisabled && (!inClinicAppointment || !inClinicAppointment.hasTimeSlots)
  };
};
